<template>
  <div class="pageContainer">
    <BannerComponent>
      <button
          class="banner-toggle-btn"
          @click="toggleSubscription"
          :aria-expanded="subscriptionOpen"
      >
        <font-awesome-icon
            :icon="['fas', subscriptionOpen ? 'chevron-down' : 'chevron-right']"
        />
      </button>
      <h6 class="banner-heading">Subscription</h6>
    </BannerComponent>

    <div class="card cardBackground" v-if="subscriptionOpen">
      <div class="cardLine">
        <p>Subscription:</p>
        <p>{{ subscriptionType === 'free' ? 'Free Plan' : 'Device Plan' }}</p>
      </div>
      <div class="lineDiv"></div>
      <div class="cardLine">
        <p>Next Payment:</p>
        <p>{{subscriptionData.nextPaymentDate.format('MM/DD/YYYY')}}</p>
      </div>
      <!--<div class="lineDiv"></div>
      <div class="cardLine">
        <p>Payment Method:</p>
        <p>{{subscriptionData.paymentMethod}}</p>
      </div>
      <div class="lineDiv"></div>
      <div class="cardLine">
        <p>Protection Plan:</p>
        <p>{{subscriptionData.protectionPlanType}}</p>
      </div>
      <div class="lineDiv"></div>-->
    </div>

    <button class="primaryButton" v-if="subscriptionOpen && subscriptionType === 'free'" @click="openModal('update')" style="margin-top: 0;">Update Plan</button>

    <BannerComponent>
      <button
          class="banner-toggle-btn"
          @click="toggleBilling"
          :aria-expanded="billingOpen"
      >
        <font-awesome-icon
            :icon="['fas', billingOpen ? 'chevron-down' : 'chevron-right']"
        />
      </button>
      <h6 class="banner-heading">Billing</h6>
    </BannerComponent>

    <div class="card cardBackground" v-if="billingOpen">
      <div class="cardLine">
        <p>Monthly Bill:</p>
        <p>${{billingData.monthlyPrice.toFixed(2)}}</p>
      </div>
      <div class="lineDiv"></div>
      <div class="cardLine">
        <p>Next Payment:</p>
        <p>{{billingData.nextPaymentDate.format('MM/DD/YYYY')}}</p>
      </div>
      <div class="lineDiv"></div>
      <div class="cardLine">
        <p>Payment Method:</p>
        <p>{{billingData.paymentMethod}}</p>
      </div>
<!--      <div class="lineDiv"></div>
      <div class="cardLine">
        <p>Auto Pay:</p>
        <p>{{billingData.autoPayActive ? 'Active' : 'Inactive'}}</p>
      </div>-->
      <div class="lineDiv"></div>
      <div class="cardLine">
        <p>Billing Address:</p>
        <p>{{billingData.billingAddress}}</p>
      </div>
      <div class="lineDiv"></div>
    </div>

    <button class="primaryButton" v-if="billingOpen" @click="openModal('billing')" style="margin-top: 0;">Manage Billing</button>

    <BannerComponent>
      <button
          class="banner-toggle-btn"
          @click="togglePaymentMethods"
          :aria-expanded="paymentMethodsOpen"
      >
        <font-awesome-icon
            :icon="['fas', paymentMethodsOpen ? 'chevron-down' : 'chevron-right']"
        />
      </button>
      <h6 class="banner-heading">Payment Methods</h6>
    </BannerComponent>

    <div class="card cardBackground" v-if="paymentMethodsOpen">
      <div v-if="loading && !paymentMethods.length" class="loading-section">
        <p>Loading payment methods...</p>
      </div>
      
      <div v-else-if="paymentMethods.length" class="payment-methods-section">
        <div 
          v-for="pm in paymentMethods" 
          :key="pm.id"
          class="payment-method-item"
          :class="{ 'default-method': pm.is_default }"
        >
          <div class="payment-method-info">
            <div class="card-info">
              <font-awesome-icon :icon="['fas', 'credit-card']" />
              <span class="card-number">•••• •••• •••• {{ pm.card.last4 }}</span>
              <span class="card-expiry">{{ pm.card.exp_month }}/{{ pm.card.exp_year }}</span>
            </div>
            <div class="card-details">
              <div class="card-brand">{{ pm.card.brand.toUpperCase() }}</div>
              <div v-if="pm.is_default" class="default-badge">Default</div>
            </div>
          </div>
          <div class="payment-method-actions" v-if="!pm.is_default">
            <button 
              class="action-button primary" 
              @click="setAsDefaultPaymentMethod(pm.id)"
              :disabled="loading"
            >
              Set Default
            </button>
            <button 
              class="action-button danger" 
              @click="removePaymentMethod(pm.id)"
              :disabled="loading"
            >
              Remove
            </button>
          </div>
        </div>
      </div>
      
      <div v-else class="no-payment-methods">
        <p>No payment methods found.</p>
      </div>
      
      <!-- Add Payment Method Button -->
      <div class="add-payment-method-section">
        <button 
          class="add-payment-method-button primary-button" 
          @click="openAddPaymentMethodModal"
          :disabled="loading || addingPaymentMethod"
        >
          <font-awesome-icon :icon="['fas', 'plus']" />
          Add Payment Method
        </button>
      </div>
    </div>

    <BannerComponent>
      <button
          class="banner-toggle-btn"
          @click="toggleInvoices"
          :aria-expanded="invoicesOpen"
      >
        <font-awesome-icon
            :icon="['fas', invoicesOpen ? 'chevron-down' : 'chevron-right']"
        />
      </button>
      <h6 class="banner-heading">Invoice History</h6>
    </BannerComponent>

    <div class="card cardBackground" v-if="invoicesOpen">
      <div v-if="loading && !invoices.length" class="loading-section">
        <p>Loading invoices...</p>
      </div>
      
      <div v-else-if="invoices.length" class="invoices-section">
        <div 
          v-for="invoice in (showAllInvoices ? invoices : invoices.slice(0, 5))" 
          :key="invoice.id"
          class="invoice-item"
        >
          <div class="invoice-info">
            <div class="invoice-date">{{ formatDate(invoice.created) }}</div>
            <div class="invoice-number">#{{ invoice.number || invoice.id.substring(3, 10) }}</div>
            <div class="invoice-amount">${{ formatAmount(invoice.amount_paid || invoice.total, 'usd') }}</div>
            <div class="invoice-status" :class="invoice.status">
              {{ invoice.status === 'paid' ? 'Paid' : invoice.status }}
            </div>
          </div>
          <div class="invoice-actions">
            <a 
              v-if="invoice.hosted_invoice_url" 
              :href="invoice.hosted_invoice_url" 
              target="_blank" 
              class="invoice-link"
            >
              <font-awesome-icon :icon="['fas', 'external-link-alt']" />
              View
            </a>
            <a 
              v-if="invoice.invoice_pdf" 
              :href="invoice.invoice_pdf" 
              target="_blank" 
              class="invoice-link"
            >
              <font-awesome-icon :icon="['fas', 'download']" />
              PDF
            </a>
          </div>
        </div>
        
        <div class="show-more-section" v-if="invoices.length > 5">
          <button class="show-more-button" @click="showAllInvoices = !showAllInvoices">
            {{ showAllInvoices ? 'Show Less' : `Show All (${invoices.length})` }}
          </button>
        </div>
      </div>
      
      <div v-else class="no-invoices">
        <p>No invoices found.</p>
      </div>
    </div>

    <BaseModal
        :visible="modal.open"
        :cancel-show="false"
        :submit-color="modal.submitColor"
        :submit-border-color="modal.submitColorBorder"
        :title="modal.title"
        :submit-label="modal.submitLabel"
        @close="closeModal"
        @submit="submitModal"
    >
      <div class="modalContainer" v-if="modal.mode === 'update'">
        <div class="current-plan-section">
          <h4>Current Plan</h4>
          <div class="current-plan-info">
            <p><strong>Plan:</strong> {{ subscriptionType === 'free' ? 'Free Plan' : 'Device Plan' }}</p>
            <p><strong>Status:</strong> {{ subscriptionData.protectionPlanType }}</p>
            <p v-if="subscriptionData.nextPaymentDate"><strong>Next billing:</strong> {{ subscriptionData.nextPaymentDate.format('MMM DD, YYYY') }}</p>
          </div>
        </div>
        
        <div class="available-plans-section" v-if="products.length">
          <h4>Available Plans</h4>
          <div class="plans-list">
            <div 
              v-for="product in products" 
              :key="product.id"
              class="plan-option"
              :class="{ 'current-plan': isCurrentProduct(product) }"
            >
              <div class="plan-details">
                <h5>{{ product.name }}</h5>
                <p class="plan-description">{{ product.description }}</p>
                <div class="plan-price" v-if="product.default_price && product.default_price.recurring">
                  ${{ formatAmount(product.default_price.unit_amount, 'usd') }}
                  /{{ product.default_price.recurring.interval }}
                </div>
              </div>
              <div class="plan-actions">
                <button 
                  v-if="!isCurrentProduct(product)" 
                  class="action-button"
                  :disabled="isChangingSubscription"
                  @click="changeToProduct(product)"
                >
                  {{ isChangingSubscription ? 'Updating...' : getChangeActionText(product) }}
                </button>
                <span v-else class="current-label">Current Plan</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modalContainer" v-if="modal.mode === 'billing'">
        <div class="modalLine">
          <div class="text-box-label" style="margin-top: .5rem;">
            Name:
          </div>
          <p style="margin-left: 15px">{{billingData.name}}</p>
        </div>
        <div class="modalLine">
          <div class="text-box-label" style="margin-top: .5rem;">
            Address:
          </div>
          <p style="margin-left: 15px">{{billingData.billingAddress}}</p>
        </div>
        <div class="modalLine">
          <div class="text-box-label" style="margin-top: .5rem;">
            Payment:
          </div>
          <p style="margin-left: 15px">{{billingData.paymentMethod}}</p>
        </div>
<!--        <div class="modalLine" style="flex-direction: row">
          <div class="text-box-label" >
            Auto Pay:
          </div>
          <CustomSwitch :disabled="true" :small="true" v-model="billingData.autoPayActive"></CustomSwitch>
        </div>-->
      </div>
      <div class="modalContainer" v-if="modal.mode === 'billingEdit'">
        <div class="billing-edit-section">
          <h4>Personal Information</h4>
          <div class="modalLine">
            <div class="text-box-label">
              Name:
            </div>
            <input type="text" v-model="editBillingData.name" placeholder="Full name">
          </div>
          <div class="modalLine">
            <div class="text-box-label">
              Email:
            </div>
            <input type="email" v-model="editBillingData.email" placeholder="email@example.com">
          </div>
          <div class="modalLine">
            <div class="text-box-label">
              Phone:
            </div>
            <input type="tel" v-model="editBillingData.phone" placeholder="+1 (555) 123-4567">
          </div>
        </div>

        <div class="billing-edit-section">
          <h4>Billing Address</h4>
          <div class="modalLine">
            <div class="text-box-label">
              Address Line 1: <span class="required">*</span>
            </div>
            <input type="text" v-model="editBillingData.address.line1" placeholder="Street address" required>
          </div>
          <div class="modalLine">
            <div class="text-box-label">
              Address Line 2:
            </div>
            <input type="text" v-model="editBillingData.address.line2" placeholder="Apartment, suite, etc.">
          </div>
          <div class="modalLine">
            <div class="text-box-label">
              City: <span class="required">*</span>
            </div>
            <input type="text" v-model="editBillingData.address.city" placeholder="City" required>
          </div>
          <div class="modal-row">
            <div class="modalLine half-width">
              <div class="text-box-label">
                State/Province:
              </div>
              <input type="text" v-model="editBillingData.address.state" placeholder="State/Province">
            </div>
            <div class="modalLine half-width">
              <div class="text-box-label">
                Postal Code: <span class="required">*</span>
              </div>
              <input type="text" v-model="editBillingData.address.postal_code" placeholder="ZIP/Postal Code" required>
            </div>
          </div>
          <div class="modalLine">
            <div class="text-box-label">
              Country: <span class="required">*</span>
            </div>
            <select v-model="editBillingData.address.country" required>
              <option value="">Select Country</option>
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
              <option value="AU">Australia</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="IT">Italy</option>
              <option value="ES">Spain</option>
              <option value="JP">Japan</option>
              <option value="BR">Brazil</option>
              <!-- Add more countries as needed -->
            </select>
          </div>
        </div>

        <div class="billing-edit-section" v-if="paymentMethods.length > 1">
          <h4>Default Payment Method</h4>
          <div class="modalLine">
            <div class="text-box-label">
              Payment Method:
            </div>
            <select v-model="editBillingData.selectedPaymentMethodId">
              <option 
                v-for="pm in paymentMethods" 
                :key="pm.id" 
                :value="pm.id"
              >
                •••• •••• •••• {{ pm.card.last4 }} ({{ pm.card.brand.toUpperCase() }}){{ pm.is_default ? ' - Current Default' : '' }}
              </option>
            </select>
          </div>
        </div>
        
      </div>
    </BaseModal>

    <!-- Add Payment Method Modal -->
    <BaseModal
      :visible="addPaymentMethodModalOpen"
      title="Add Payment Method"
      submit-label="Add"
      :submit-disabled="!canAddPaymentMethod || addingPaymentMethod"
      @submit="addPaymentMethod"
      @cancel="closeAddPaymentMethodModal"
      @close="closeAddPaymentMethodModal"
    >
      <div class="payment-method-modal-content">
        <!-- Card Information -->
        <div class="payment-form-section">
          <h5>Card Information</h5>
          <div class="modalLine">
            <div class="text-box-label">
              Card Details: <span class="required">*</span>
            </div>
            <div id="card-element" class="stripe-card-element" :class="{ disabled: addingPaymentMethod }">
              <!-- Stripe Elements will be mounted here -->
            </div>
          </div>
          <div v-if="addPaymentError" class="error-message">
            {{ addPaymentError }}
          </div>
        </div>

        <!-- Billing Details for New Payment Method -->
        <div class="payment-form-section">
          <h5>Billing Details</h5>

          <!-- Same Billing Address Checkbox -->
          <div class="modalLine" style="margin-bottom: 0">
            <div class="checkbox-container">
              <input
                  type="checkbox"
                  id="useSameBillingAddress"
                  v-model="useSameBillingAddress"
                  @change="handleBillingAddressChange"
                  :disabled="addingPaymentMethod"
              >
              <label for="useSameBillingAddress" class="checkbox-label">
                Use same billing address as subscription
              </label>
            </div>
          </div>

          <div class="modalLine" v-if="!useSameBillingAddress">
            <div class="text-box-label">
              Cardholder Name: <span class="required">*</span>
            </div>
            <input 
              type="text" 
              v-model="newPaymentMethod.name" 
              placeholder="Full name on card"
              :disabled="addingPaymentMethod"
            >
          </div>
          <div class="modalLine" v-if="!useSameBillingAddress">
            <div class="text-box-label">
              Email:
            </div>
            <input 
              type="email" 
              v-model="newPaymentMethod.email" 
              placeholder="email@example.com"
              :disabled="addingPaymentMethod"
            >
          </div>

          <!-- Custom Address Fields (shown only when not using same address) -->
          <div v-if="!useSameBillingAddress">
            <div class="modalLine">
              <div class="text-box-label">
                Address Line 1: <span class="required">*</span>
              </div>
              <input 
                type="text" 
                v-model="newPaymentMethod.address.line1" 
                placeholder="Street address"
                :disabled="addingPaymentMethod"
              >
            </div>
            <div class="modal-row">
              <div class="modalLine half-width">
                <div class="text-box-label">
                  City: <span class="required">*</span>
                </div>
                <input 
                  type="text" 
                  v-model="newPaymentMethod.address.city" 
                  placeholder="City"
                  :disabled="addingPaymentMethod"
                >
              </div>
              <div class="modalLine half-width">
                <div class="text-box-label">
                  ZIP Code: <span class="required">*</span>
                </div>
                <input 
                  type="text" 
                  v-model="newPaymentMethod.address.postal_code" 
                  placeholder="ZIP Code"
                  :disabled="addingPaymentMethod"
                >
              </div>
            </div>
          </div>
          
          <!-- Display current subscription address when using same address -->
          <div v-else class="current-address-display">
            <div class="text-box-label">Billing Address:</div>
            <div class="address-preview">
              <div v-if="billingData.name">{{ billingData.name }}</div>
              <div>{{ billingData.billingAddress || 'No address on file' }}</div>
              <div v-if="billingData.email">{{ billingData.email }}</div>
            </div>
          </div>
        </div>
      </div>
    </BaseModal>

    <MessageModal :message="appMessage.text" :type="appMessage.type" ref="messageModal" />
    <LoadingPage :is-visible="loading"/>
  </div>
</template>

<script>
import LoadingPage from "@/components/LoadingPage.vue";
import MessageModal from "@/components/MessageModal.vue";
import BannerComponent from "@/components/BannerComponent.vue";
import dayjs from "dayjs";
import BaseModal from "@/components/BaseModal.vue";
//import CustomSwitch from "@/components/CustomSwitch.vue";
import PaymentsAPIService from "@/servicehandlers/PaymentsAPIService";
import {useLoginStore} from "@/stores/loginStore";
const paymentsAPIService = new PaymentsAPIService();

export default {
  name: 'SettingsPage',
  components: {
    //CustomSwitch,
    BaseModal,
    BannerComponent,
    MessageModal,
    LoadingPage,
  },
  computed: {
    subscriptionType() {
      return useLoginStore().getSubscriptionType
    },
    canAddPaymentMethod() {
      // Check if cardholder name is provided
      let hasPaymentInfo = false
      
      if (this.useSameBillingAddress) {
        // When using same billing address, check if billing data has name
        hasPaymentInfo = this.billingData.name && this.billingData.name.trim()
        
        // Fallback to newPaymentMethod name if billing data doesn't have name
        if (!hasPaymentInfo) {
          hasPaymentInfo = this.newPaymentMethod.name && this.newPaymentMethod.name.trim()
        }
      } else {
        // When using custom data, check newPaymentMethod name
        hasPaymentInfo = this.newPaymentMethod.name && this.newPaymentMethod.name.trim()
      }
      
      // Check if address is available (either same as billing or custom)
      let hasAddress = false
      
      if (this.useSameBillingAddress) {
        // When using same billing address, check if billing data has required fields
        hasAddress = this.billingData.address && 
                    this.billingData.address.line1 && 
                    this.billingData.address.city && 
                    this.billingData.address.postal_code
        
        // If billing data is incomplete, check if newPaymentMethod has the address populated
        if (!hasAddress) {
          hasAddress = this.newPaymentMethod.address && 
                      this.newPaymentMethod.address.line1 && 
                      this.newPaymentMethod.address.city && 
                      this.newPaymentMethod.address.postal_code
        }
      } else {
        // When using custom address, check newPaymentMethod address fields
        hasAddress = this.newPaymentMethod.address && 
                    this.newPaymentMethod.address.line1 && 
                    this.newPaymentMethod.address.city && 
                    this.newPaymentMethod.address.postal_code
      }
      
      // Note: We don't check card details here since Stripe handles validation
      return hasPaymentInfo && hasAddress && !this.addingPaymentMethod
    },
  },
  data(){
    return {
      appMessage: {
        text: '',
        type: 'error',
      },
      subscriptionOpen: true,
      paymentMethodsOpen: true,
      invoicesOpen: false,
      subscriptionData: {
        nextPaymentDate: dayjs(),
        paymentMethod: '',
        protectionPlanType: ''
      },
      billingOpen: true,
      billingData: {
        monthlyPrice: 0,
        nextPaymentDate: dayjs(),
        paymentMethod: '',
        autoPayActive: true,
        billingAddress: '',
        name: '',
        email: '',
        phone: '',
        address: {
          line1: '',
          line2: '',
          city: '',
          state: '',
          postal_code: '',
          country: ''
        }
      },
      editBillingData: {},
      modal: {
        open: false,
        mode: '',
        title: '',
        submitColor: '',
        submitColorBorder: '',
        submitLabel: '',
      },
      error: null,
      success: false,
      loading: false,
      paymentMethods: [],
      products: [],
      subscriptions: [],
      subscription_schedules: [],
      upcoming_subscriptions: [],
      invoices: [],
      payments: [],
      access_token: null,
      apiUrl: process.env.VUE_APP_API_URL,
      isChangingSubscription: false,
      currentSubscriptionId: null,
      showAllInvoices: false,
      addingPaymentMethod: false,
      addPaymentError: '',
      useSameBillingAddress: true,
      newPaymentMethod: {
        type: 'card',
        // Common fields
        name: '',
        email: '',
        address: {
          line1: '',
          city: '',
          postal_code: ''
        }
      },
      addPaymentMethodModalOpen: false,
      // Stripe integration
      stripe: null,
      elements: null,
      cardElement: null,
      clientSecret: '',
      paymentMethodTypeOptions: [
        {
          id: 'card',
          name: 'Credit/Debit Card'
        }
      ],
    }
  },
  async mounted(){
    console.clear()
    
    try {
      // Load payment methods first to get billing details
      await this.loadPaymentMethods()
      
      // Load payment info for subscription display (no longer modifies billing data)
      await this.getPaymentInfo()
      
      // Then load subscription info - await to prevent race conditions
      await this.getSubscriptionInfo()
      
    } catch (error) {
      console.error('Error during component mounting:', error)
    }
    
    // Initialize Stripe for payment method management
    if (!window.Stripe) {
      const script = document.createElement('script')
      script.src = 'https://js.stripe.com/v3/'
      script.onload = this.initStripe
      document.head.appendChild(script)
    } else {
      this.initStripe()
    }
  },
  methods: {
    showError(e){
      this.loading = false;
      this.isChangingSubscription = false;
      this.appMessage.text = 'Error: ' + (e.message || e)
      this.appMessage.type = 'error'
      this.$refs.messageModal.open()
    },
    
    // Stripe integration methods
    async initStripe() {
      try {
        if (!window.Stripe) {
          console.error('Stripe.js is not loaded');
          this.addPaymentError = 'Payment system not available. Please refresh the page.';
          return;
        }
        
        if (!process.env.VUE_APP_STRIPE_PUBLIC_KEY) {
          console.error('Stripe public key not configured');
          this.addPaymentError = 'Payment system configuration error.';
          return;
        }
        
        console.log('Initializing Stripe with key:', process.env.VUE_APP_STRIPE_PUBLIC_KEY.substring(0, 8) + '...');
        
        this.stripe = window.Stripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY);
        this.elements = this.stripe.elements();
        
        // Create card element with custom styling to match the app
        const style = {
          base: {
            fontSize: '16px',
            color: '#ffffff',
            fontFamily: 'inherit',
            '::placeholder': {
              color: '#888888'
            }
          },
          invalid: {
            color: '#fa755a',
            iconColor: '#fa755a'
          }
        };
        
        this.cardElement = this.elements.create('card', { style });
        
        console.log('Stripe initialized successfully');
      } catch (error) {
        console.error('Error initializing Stripe:', error);
        this.addPaymentError = 'Failed to initialize payment system';
      }
    },
    
    mountCardElement() {
      if (!this.cardElement) {
        console.error('Card element not initialized');
        this.addPaymentError = 'Payment system not ready. Please try again.';
        return;
      }
      
      // Check if the DOM element exists
      const targetElement = document.getElementById('card-element');
      if (!targetElement) {
        console.error('Card element DOM target not found');
        this.addPaymentError = 'Payment form element not found';
        return;
      }
      
      try {
        this.cardElement.mount('#card-element');
        console.log('Card element mounted successfully');
        
        // Add event listeners for better debugging
        this.cardElement.on('ready', () => {
          console.log('Card element is ready');
        });
        
        this.cardElement.on('change', (event) => {
          if (event.error) {
            this.addPaymentError = event.error.message;
          } else {
            this.addPaymentError = '';
          }
        });
        
      } catch (error) {
        console.error('Error mounting card element:', error);
        this.addPaymentError = 'Failed to load payment form: ' + error.message;
      }
    },
    
    unmountCardElement() {
      if (this.cardElement) {
        try {
          // Remove event listeners before unmounting
          this.cardElement.off('ready');
          this.cardElement.off('change');
          
          this.cardElement.unmount();
          console.log('Card element unmounted');
        } catch (error) {
          console.error('Error unmounting card element:', error);
          // Don't show this error to user as it's not critical
        }
      }
    },
    
    async getSetupIntent() {
      try {
        const response = await paymentsAPIService.getIntents(this.$router);
        this.clientSecret = response.clientSecret;
        console.log('Setup intent obtained');
      } catch (error) {
        console.error('Error getting setup intent:', error);
        this.addPaymentError = 'Failed to initialize payment setup';
      }
    },
    
    async getPaymentInfo() {
      try {
        const response = await paymentsAPIService.getPaymentMethods(this.$router)
        console.log(response)
        
        // Only set the subscription data payment method for display
        // Billing data payment method is handled by loadBillingDetailsFromPaymentMethods()
        if (response && response.length > 0) {
          const pm = response[0].card.last4;
          this.subscriptionData.paymentMethod = `•••• •••• •••• ${pm}`
        }
      } catch (error) {
        console.error('Error loading payment info:', error)
        // Don't modify paymentMethods array here - that's handled by loadPaymentMethods()
      }
    },
    async getSubscriptionInfo() {
      this.loading = true;
      try {
        const subs = await paymentsAPIService.getSubscriptions(this.$router);
        console.log(subs)
        if (!subs?.length) {
          return this.resetSubscriptionData();
        }
        const sub  = subs[0];
        this.currentSubscriptionId = sub.id;
        const item = sub.items.data[0];
        this.subscriptionData.nextPaymentDate =
            dayjs(item.current_period_end * 1000);

        this.subscriptionData.protectionPlanType =
            sub.status === 'active' ? 'Active' : 'Inactive';

        this.billingData.monthlyPrice = parseFloat(item.price.unit_amount) / 100

        // Billing details are now loaded from payment methods in loadBillingDetailsFromPaymentMethods()
        // If we have a default payment method in the subscription, we can get the billing details from it as a fallback
        if (sub.default_payment_method && sub.default_payment_method.billing_details && !this.paymentMethods.length) {
          let billingDetails = sub.default_payment_method.billing_details
          const address = billingDetails.address || {};
          const addressParts = [
            address.line1,
            address.line2 && address.line2.trim() ? address.line2 : null,
            address.city,
            address.state && address.postal_code ? `${address.state} ${address.postal_code}` : address.state || address.postal_code
          ].filter(part => part && part.trim()).join(', ');
          
          this.billingData.billingAddress = addressParts || 'No address on file'
          this.billingData.name = billingDetails.name || 'No name on file'
          this.billingData.email = billingDetails.email || ''
          this.billingData.phone = billingDetails.phone || ''
          
          // Populate individual address fields
          this.billingData.address = {
            line1: address.line1 || '',
            line2: address.line2 || '',
            city: address.city || '',
            state: address.state || '',
            postal_code: address.postal_code || '',
            country: address.country || ''
          }
        }

      } catch (e) {
        this.showError(e);
      } finally {
        this.loading = false;
      }
    },
    toggleSubscription(){
      this.subscriptionOpen = !this.subscriptionOpen
    },
    toggleBilling(){
      this.billingOpen = !this.billingOpen
    },
    toggleAdvanced(){
      this.advancedOpen = !this.advancedOpen
    },
    togglePaymentMethods(){
      this.paymentMethodsOpen = !this.paymentMethodsOpen
    },
    toggleInvoices(){
      this.invoicesOpen = !this.invoicesOpen
    },
    openModal(mode){
      this.modal.mode = mode
      if (mode === 'update'){
        this.modal.title = 'Subscription & Plan'
        this.modal.submitColor = 'var(--color-green-button)'
        this.modal.submitColorBorder = 'var(--color-green-button-border)'
        this.modal.submitLabel = 'Update'
        this.modal.open = true
      }
      else if (mode === 'billing'){
        this.modal.title = 'Billing'
        this.modal.submitLabel = 'Edit'
        this.modal.open = true
      }
    },
    closeModal(){
      this.modal = {
        open: false,
        mode: '',
        title: '',
        submitColor: '',
        submitColorBorder: '',
      }
    },
    async submitModal(){
      if (this.modal.mode === 'billing'){
        // Prepare edit data by deep copying current billing data
        this.editBillingData = JSON.parse(JSON.stringify(this.billingData))
        
        // Load current payment methods for selection
        await this.loadPaymentMethods()
        
        // Set current default payment method if available
        const defaultPM = this.paymentMethods.find(pm => pm.is_default)
        if (defaultPM) {
          this.editBillingData.selectedPaymentMethodId = defaultPM.id
        }
        
        this.modal.mode = 'billingEdit'
        this.modal.submitLabel = 'Save'
      }
      else if (this.modal.mode === 'billingEdit'){
        await this.saveBillingChanges()
      }
      else {
        console.log('submit')
      }
    },
    
    async saveBillingChanges() {
      if (!this.validateBillingForm()) {
        return
      }
      
      try {
        this.loading = true
        
        // Update customer information
        const customerData = {
          name: this.editBillingData.name,
          email: this.editBillingData.email,
          phone: this.editBillingData.phone,
          address: {
            line1: this.editBillingData.address.line1,
            line2: this.editBillingData.address.line2 || null,
            city: this.editBillingData.address.city,
            state: this.editBillingData.address.state || null,
            postal_code: this.editBillingData.address.postal_code,
            country: this.editBillingData.address.country
          }
        }
        
        await paymentsAPIService.updateCustomer(customerData, this.$router)
        
        // Update payment method billing details if we have a selected payment method
        if (this.editBillingData.selectedPaymentMethodId) {
          const paymentMethodData = {
            billing_details: {
              name: this.editBillingData.name,
              email: this.editBillingData.email,
              phone: this.editBillingData.phone,
              address: {
                line1: this.editBillingData.address.line1,
                line2: this.editBillingData.address.line2 || null,
                city: this.editBillingData.address.city,
                state: this.editBillingData.address.state || null,
                postal_code: this.editBillingData.address.postal_code,
                country: this.editBillingData.address.country
              }
            }
          }
          
          await paymentsAPIService.updatePaymentMethod(this.editBillingData.selectedPaymentMethodId, paymentMethodData, this.$router)
          
          // Set as default payment method if it's different from current default
          const currentDefault = this.paymentMethods.find(pm => pm.is_default)
          if (!currentDefault || currentDefault.id !== this.editBillingData.selectedPaymentMethodId) {
            await paymentsAPIService.setDefaultPaymentMethod({ paymentMethodId: this.editBillingData.selectedPaymentMethodId }, this.$router)
          }
        }
        
        // Reload data to reflect changes
        await this.getSubscriptionInfo()
        await this.loadPaymentMethods()
        
        this.appMessage.text = 'Billing information updated successfully!'
        this.appMessage.type = 'success'
        this.$refs.messageModal.open()
        
        this.editBillingData = {}
        this.closeModal()
        
      } catch (e) {
        this.showError('Failed to update billing information: ' + (e.message || e))
      } finally {
        this.loading = false
      }
    },
    
    validateBillingForm() {
      if (!this.editBillingData.name || !this.editBillingData.name.trim()) {
        this.showError('Name is required')
        return false
      }
      
      if (!this.editBillingData.address.line1 || !this.editBillingData.address.line1.trim()) {
        this.showError('Address Line 1 is required')
        return false
      }
      
      if (!this.editBillingData.address.city || !this.editBillingData.address.city.trim()) {
        this.showError('City is required')
        return false
      }
      
      if (!this.editBillingData.address.postal_code || !this.editBillingData.address.postal_code.trim()) {
        this.showError('Postal Code is required')
        return false
      }
      
      if (!this.editBillingData.address.country || !this.editBillingData.address.country.trim()) {
        this.showError('Country is required')
        return false
      }
      
      if (this.editBillingData.email && !this.isValidEmail(this.editBillingData.email)) {
        this.showError('Please enter a valid email address')
        return false
      }
      
      return true
    },
    
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    
    productInfo(productId) {
      return this.products.find( (p) => p.id === productId) || null
    },

    async loadPaymentMethods() {
      try {
        const response = await paymentsAPIService.getPaymentMethods(this.$router)
        console.log('loadPaymentMethods', response)
        this.paymentMethods = response || []
        
        // Load billing details from payment methods after they're loaded
        this.loadBillingDetailsFromPaymentMethods()
      } catch (error) {
        this.paymentMethods = []  // fallback if API call fails
      }
    },

    loadBillingDetailsFromPaymentMethods() {
      try {
        // Check if payment methods are available
        if (!this.paymentMethods || !Array.isArray(this.paymentMethods) || this.paymentMethods.length === 0) {
          console.warn('No payment methods available to load billing details from')
          this.setBillingDataFallback()
          return
        }

        // Find the default payment method
        const defaultPaymentMethod = this.paymentMethods.find(pm => pm.is_default) || this.paymentMethods[0]
        
        if (!defaultPaymentMethod) {
          console.warn('No default payment method found')
          this.setBillingDataFallback()
          return
        }

        // Check if billing details exist
        if (!defaultPaymentMethod.billing_details) {
          console.warn('Payment method has no billing details')
          this.setBillingDataFallback()
          return
        }

        const billingDetails = defaultPaymentMethod.billing_details
        const address = billingDetails.address || {}

        // Safely construct billing address with null checks
        const addressParts = [
          address.line1,
          address.line2 && address.line2.trim() ? address.line2 : null,
          address.city,
          address.state && address.postal_code ? `${address.state} ${address.postal_code}` : address.state || address.postal_code
        ].filter(part => part && part.trim()).join(', ');
        
        // Safeguard: Only update if we have valid billing details
        if (billingDetails && (billingDetails.name || billingDetails.email || address.line1)) {
          // Update billing data with information from payment method
          this.billingData.billingAddress = addressParts || 'No address on file'
          this.billingData.name = billingDetails.name || 'No name on file'
          this.billingData.email = billingDetails.email || ''
          this.billingData.phone = billingDetails.phone || ''
          
          // Also set the payment method for billing display
          if (defaultPaymentMethod.card && defaultPaymentMethod.card.last4) {
            this.billingData.paymentMethod = `•••• •••• •••• ${defaultPaymentMethod.card.last4}`
          }
          
          // Populate individual address fields
          this.billingData.address = {
            line1: address.line1 || '',
            line2: address.line2 || '',
            city: address.city || '',
            state: address.state || '',
            postal_code: address.postal_code || '',
            country: address.country || ''
          }

          console.log('Billing details loaded from payment method:', this.billingData)
        } else {
          console.warn('Payment method has incomplete billing details, using fallback')
          this.setBillingDataFallback()
        }
      } catch (error) {
        console.error('Error loading billing details from payment methods:', error)
        this.setBillingDataFallback()
      }
    },

    setBillingDataFallback() {
      // Set fallback values when billing details cannot be loaded
      this.billingData.billingAddress = 'No address on file'
      this.billingData.name = 'No name on file'
      this.billingData.email = ''
      this.billingData.phone = ''
      this.billingData.address = {
        line1: '',
        line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: ''
      }
      console.log('Set billing data to fallback values')
    },

    async loadProducts() {
      try {
        const response = await paymentsAPIService.getProducts(this.$router)
        console.log('loadProducts', response)
        this.products = response || []
      } catch (error) {
        this.products = []  // fallback if API call fails
      }
    },
    async loadSubscriptions() {
      try {
        const response = await paymentsAPIService.getSubscriptions(this.$router)
        console.log('loadSubscriptions', response)
        this.subscriptions = response || []
      } catch (error) {
        this.subscriptions = []  // fallback if API call fails
      }
    },
    async loadSubscriptionSchedules() {
      try {
        const response = await paymentsAPIService.getSubscriptionSchedules(this.$router)
        console.log('loadSubscriptionSchedules', response)
        this.subscription_schedules = response || []

        await this.loadPriceDetailsForSchedules()
      } catch (error) {
        this.subscription_schedules = []  // fallback if API call fails
      }
    },
    async loadPriceDetailsForSchedules() {
      const uniquePriceIds = new Set()

      // Collect all unique price IDs from all phases
      this.subscription_schedules.forEach(schedule => {
        schedule.phases.forEach(phase => {
          phase.items.forEach(item => {
            if (item.price) uniquePriceIds.add(item.price)
          })
        })
      })

      const priceIdList = Array.from(uniquePriceIds)
      const priceDataMap = {}

      // Get details for each price
      for (const priceId of priceIdList) {
        try {
          const price = await paymentsAPIService.getPrice(priceId, this.$router)
          priceDataMap[priceId] = price
        } catch (e) {
          console.error(`Failed to load price ${priceId}`, e)
        }
      }

      // Attach price data back to the schedule phases
      this.subscription_schedules.forEach(schedule => {
        schedule.phases.forEach(phase => {
          phase.items.forEach(item => {
            item.price_data = priceDataMap[item.price] || null
          })
        })
      })
    },

    async loadUpcomingSubscriptions() {
      try {
        const response = await paymentsAPIService.getUpcomingSubscriptions(this.$router)
        console.log('loadUpcomingSubscriptions', response)
        this.upcoming_subscriptions = response || []
      } catch (error) {
        this.upcoming_subscriptions = []  // fallback if API call fails
      }
    },

    async loadInvoices() {
      try {
        const response = await paymentsAPIService.getInvoices(this.$router)
        console.log('loadInvoices', response)
        this.invoices = response || []
      } catch (error) {
        this.invoices = []  // fallback if API call fails
      }
    },
    async loadPayments() {
      try {
        const response = await paymentsAPIService.getPayments(this.$router)
        console.log('loadPayments', response)
        console.log('PAYMENTS SUCCEEDED')
        this.payments = response || []
      } catch (error) {
        console.log('PAYMENTS FAILED', error)
        this.payments = []  // fallback if API call fails
      }
    },
    async changeSubscription(currentSubscriptionId, newPriceId) {

      try {
        await paymentsAPIService.changeSubscription({ currentSubscriptionId, newPriceId }, this.$router)
        await this.loadSubscriptions()
        await this.loadUpcomingSubscriptions()
      } catch (error) {
        console.log('Error changing Subscription', error)
      }

    } ,
    async setDefaultPaymentMethod( paymentMethodId ) {

      try {
        await paymentsAPIService.setDefaultPaymentMethod({ paymentMethodId }, this.$router)
        await this.loadPaymentMethods()
      } catch (error) {
        console.error('error setting default payment method', error)
      }

    },

    async deletePaymentMethod( paymentMethodId ) {
      try {
        await paymentsAPIService.deletePaymentMethod({ paymentMethodId }, this.$router)
        await this.loadPaymentMethods()
      } catch (error) {
        console.error('error deleting payment method', error)
      }

    },

    formatDate(unixTimestamp) {
      const date = new Date(unixTimestamp * 1000)

      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    },
    formatAmount(amount, currency) {
      if (amount === null) return 'N/A'

      return `${(amount / 100).toFixed(2)} ${currency}`
    },
    isCurrentProduct(product) {
      if (!this.subscriptions.length) return false;
      const currentSub = this.subscriptions[0];
      if (!currentSub.items?.data?.length) return false;
      
      const currentItem = currentSub.items.data[0];
      return currentItem && currentItem.price && currentItem.price.product === product.id;
    },
    getChangeActionText(product) {
      if (!this.subscriptions.length || !product.default_price) return 'Subscribe';
      
      const currentSub = this.subscriptions[0];
      if (!currentSub.items?.data?.length) return 'Subscribe';
      
      const currentItem = currentSub.items.data[0];
      if (!currentItem || !currentItem.price) return 'Subscribe';
      
      const currentAmount = currentItem.price.unit_amount;
      const newAmount = product.default_price.unit_amount || 0;
      
      if (newAmount > currentAmount) return 'Upgrade';
      if (newAmount < currentAmount) return 'Downgrade';
      return 'Switch Plan';
    },
    async changeToProduct(product) {
      if (!this.currentSubscriptionId || !product.default_price || !product.default_price.id) {
        this.showError('Unable to change plan. Missing subscription or product information.');
        return;
      }
      
      this.isChangingSubscription = true;
      
      try {
        await this.changeSubscription(this.currentSubscriptionId, product.default_price.id);
        
        this.appMessage.text = `Successfully changed to ${product.name}!`;
        this.appMessage.type = 'success';
        this.$refs.messageModal.open();
        
        // Refresh data
        await this.getSubscriptionInfo();
        await this.loadProducts();
        
        this.closeModal();
        
      } catch (e) {
        this.showError('Failed to change subscription: ' + (e.message || e));
      } finally {
        this.isChangingSubscription = false;
      }
    },
    resetSubscriptionData() {
      // Reset subscription-specific data
      this.subscriptionData = {
        nextPaymentDate: dayjs(),
        paymentMethod: '',
        protectionPlanType: ''
      };
      
      // Preserve billing details that were loaded from payment methods
      // Only reset subscription-specific billing fields
      const preservedBillingDetails = {
        billingAddress: this.billingData.billingAddress,
        name: this.billingData.name,
        email: this.billingData.email,
        phone: this.billingData.phone,
        address: { ...this.billingData.address }
      };
      
      this.billingData = {
        monthlyPrice: 0,
        nextPaymentDate: dayjs(),
        paymentMethod: this.billingData.paymentMethod, // Preserve payment method
        autoPayActive: true,
        ...preservedBillingDetails // Preserve all billing details
      };
      
      this.currentSubscriptionId = null;
      console.log('Reset subscription data while preserving billing details');
    },
    async setAsDefaultPaymentMethod(paymentMethodId) {
      try {
        this.loading = true;
        await paymentsAPIService.setDefaultPaymentMethod({ paymentMethodId }, this.$router);
        
        // Reload payment methods and subscription data
        await this.loadPaymentMethods();
        await this.getSubscriptionInfo();
        
        this.appMessage.text = 'Default payment method updated successfully!';
        this.appMessage.type = 'success';
        this.$refs.messageModal.open();
        
      } catch (e) {
        this.showError('Failed to set default payment method: ' + e.message);
      } finally {
        this.loading = false;
      }
    },
    async removePaymentMethod(paymentMethodId) {
      if (!confirm('Are you sure you want to remove this payment method?')) {
        return;
      }
      
      try {
        this.loading = true;
        await paymentsAPIService.deletePaymentMethod({ paymentMethodId }, this.$router);
        
        // Reload payment methods
        await this.loadPaymentMethods();
        
        this.appMessage.text = 'Payment method removed successfully!';
        this.appMessage.type = 'success';
        this.$refs.messageModal.open();
        
      } catch (e) {
        this.showError('Failed to remove payment method: ' + e.message);
      } finally {
        this.loading = false;
      }
    },
    
    
    handleBillingAddressChange() {
      if (this.useSameBillingAddress) {
        // When checking the box, populate with subscription billing data
        if (this.billingData.address) {
          this.newPaymentMethod.address = {
            line1: this.billingData.address.line1 || '',
            city: this.billingData.address.city || '',
            postal_code: this.billingData.address.postal_code || ''
          }
        }
        
        // Also populate name and email if not already set
        if (!this.newPaymentMethod.name && this.billingData.name) {
          this.newPaymentMethod.name = this.billingData.name
        }
        if (!this.newPaymentMethod.email && this.billingData.email) {
          this.newPaymentMethod.email = this.billingData.email
        }
      }
      // When unchecking, we keep the current form values (don't clear them)
      // This allows users to modify the copied address if needed
    },
    
    async addPaymentMethod() {
      if (!this.validatePaymentMethodForm()) {
        return
      }
      
      this.addingPaymentMethod = true
      this.addPaymentError = ''
      
      try {
        // Validate that we have the necessary Stripe objects
        if (!this.stripe || !this.cardElement || !this.clientSecret) {
          this.addPaymentError = 'Payment system not properly initialized. Please try refreshing the page.'
          return
        }
        
        // Determine which address to use
        const addressToUse = this.useSameBillingAddress ? this.billingData.address : this.newPaymentMethod.address
        const emailToUse = this.useSameBillingAddress && this.billingData.email ? this.billingData.email : this.newPaymentMethod.email
        
        // Use Stripe's confirmCardSetup with the setup intent
        const result = await this.stripe.confirmCardSetup(this.clientSecret, {
          payment_method: {
            card: this.cardElement,
            billing_details: {
              name: this.newPaymentMethod.name,
              email: emailToUse || undefined,
              address: {
                line1: addressToUse.line1,
                line2: addressToUse.line2 || undefined,
                city: addressToUse.city,
                state: addressToUse.state || undefined,
                postal_code: addressToUse.postal_code,
                country: addressToUse.country || 'US'
              }
            }
          }
        })
        
        console.log('Card Setup Results:', result)
        
        if (result.error) {
          this.addPaymentError = result.error.message
          return
        }
        
        // Success! Reload payment methods and show success message
        await this.loadPaymentMethods()
        await this.getSubscriptionInfo()
        
        this.appMessage.text = 'Payment method added successfully!'
        this.appMessage.type = 'success'
        this.$refs.messageModal.open()
        
        // Reset form and close modal
        this.closeAddPaymentMethodModal()
        
      } catch (error) {
        console.error('Error adding payment method:', error)
        this.addPaymentError = 'Failed to add payment method: ' + (error.message || error)
      } finally {
        this.addingPaymentMethod = false
      }
    },
    
    validatePaymentMethodForm() {
      // Cardholder name is required
      if (!this.newPaymentMethod.name.trim()) {
        this.addPaymentError = 'Cardholder name is required'
        return false
      }
      
      // Address validation - check appropriate source
      if (this.useSameBillingAddress) {
        // Validate subscription billing address
        if (!this.billingData.address || !this.billingData.address.line1) {
          this.addPaymentError = 'Subscription billing address is incomplete. Please uncheck the option and enter a custom address.'
          return false
        }
        if (!this.billingData.address.city) {
          this.addPaymentError = 'Subscription billing city is missing. Please uncheck the option and enter a custom address.'
          return false
        }
        if (!this.billingData.address.postal_code) {
          this.addPaymentError = 'Subscription billing ZIP code is missing. Please uncheck the option and enter a custom address.'
          return false
        }
      } else {
        // Validate custom address fields
        if (!this.newPaymentMethod.address.line1.trim()) {
          this.addPaymentError = 'Address is required'
          return false
        }
        
        if (!this.newPaymentMethod.address.city.trim()) {
          this.addPaymentError = 'City is required'
          return false
        }
        
        if (!this.newPaymentMethod.address.postal_code.trim()) {
          this.addPaymentError = 'ZIP code is required'
          return false
        }
      }
      
      if (this.newPaymentMethod.email && !this.isValidEmail(this.newPaymentMethod.email)) {
        this.addPaymentError = 'Please enter a valid email address'
        return false
      }
      
      return true
    },
    
    cancelAddPaymentMethod() {
      this.closeAddPaymentMethodModal()
    },
    
    resetPaymentMethodForm() {
      this.newPaymentMethod = {
        type: 'card',
        // Common fields
        name: '',
        email: '',
        address: {
          line1: '',
          city: '',
          postal_code: ''
        }
      }
      this.addPaymentError = ''
    },
    
    async openAddPaymentMethodModal() {
      this.resetPaymentMethodForm()
      
      // Get a new setup intent for this payment method creation
      await this.getSetupIntent()
      
      // If using same billing address, populate the fields
      if (this.useSameBillingAddress) {
        this.handleBillingAddressChange()
      }
      
      this.addPaymentMethodModalOpen = true
      
      // Mount the Stripe card element after the modal is open and DOM is ready
      this.$nextTick(() => {
        this.mountCardElement()
      })
    },
    
    closeAddPaymentMethodModal() {
      // Unmount the Stripe element before closing
      this.unmountCardElement()
      
      this.resetPaymentMethodForm()
      this.addPaymentMethodModalOpen = false
    },
    
  },
  
}
</script>

<style scoped>
.resetContainer{
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.pageContainer {
  gap: 0;
}

/* give each open‐panel a little breathing room */
.pageContainer > .card,
.pageContainer > .primaryButton {
  margin-top: 30px;
  margin-bottom: 30px;
}

/* Subscription Management Modal Styles */
.current-plan-section {
  margin-bottom: 30px;
  padding: 15px;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 12px;
}

.current-plan-info p {
  margin: 8px 0;
  color: var(--text-primary);
}

.available-plans-section h4 {
  margin-bottom: 15px;
  color: var(--text-primary);
}

.plans-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.plan-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.plan-option:hover {
  border-color: var(--color-primary-button);
}

.plan-option.current-plan {
  border-color: var(--color-green-button);
  background: rgba(var(--color-green-button-rgb), 0.1);
}

.plan-details h5 {
  margin: 0 0 5px 0;
  color: var(--text-primary);
  font-size: 1.1rem;
}

.plan-description {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 5px 0 10px 0;
}

.plan-price {
  font-size: 1.2rem;
  font-weight: bold;
  color: var(--color-primary-button);
}

.plan-actions .action-button {
  background: var(--color-primary-button);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  min-width: 100px;
}

.plan-actions .action-button:hover:not(:disabled) {
  background: var(--color-primary-button-hover);
}

.plan-actions .action-button:disabled {
  background: var(--color-card-border);
  cursor: not-allowed;
}

.current-label {
  color: var(--color-green-button);
  font-weight: 500;
  padding: 10px 20px;
}

@media (max-width: 768px) {
  .plan-option {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .plan-actions {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
}

/* Payment Methods Styles */
.payment-methods-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.payment-method-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  border-bottom: 1px solid var(--color-card-border);
}

.payment-method-item:last-child {
  border-bottom: none;
}

.payment-method-item.default-method {
  background: rgba(var(--color-green-button-rgb), 0.1);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 8px;
}

.payment-method-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-grow: 1;
}

.card-info {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-primary);
}

.card-number {
  font-family: monospace;
  font-size: 1.1rem;
}

.card-expiry {
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.card-details {
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-brand {
  background: var(--color-card-border);
  color: var(--text-primary);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
}

.default-badge {
  background: var(--color-green-button);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
}

.payment-method-actions {
  display: flex;
  gap: 8px;
}

.action-button {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  min-width: 90px;
}

.action-button.primary {
  background: var(--color-primary-button);
  color: white;
}

.action-button.primary:hover:not(:disabled) {
  background: var(--color-primary-button-hover);
}

.action-button.danger {
  background: var(--color-red-button);
  color: white;
}

.action-button.danger:hover:not(:disabled) {
  background: var(--color-red-button-hover);
}

.action-button:disabled {
  background: var(--color-card-border);
  cursor: not-allowed;
}

.no-payment-methods {
  text-align: center;
  padding: 20px;
  color: var(--text-secondary);
}

/* Invoice History Styles */
.loading-section, .no-invoices {
  text-align: center;
  padding: 20px;
  color: var(--text-secondary);
}

.invoices-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.invoice-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid var(--color-card-border);
}

.invoice-item:last-child {
  border-bottom: none;
}

.invoice-info {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-grow: 1;
}

.invoice-date {
  color: var(--text-primary);
  font-weight: 500;
  min-width: 120px;
}

.invoice-number {
  color: var(--text-secondary);
  font-family: monospace;
  min-width: 80px;
}

.invoice-amount {
  color: var(--color-green-button);
  font-weight: bold;
  font-size: 1.1rem;
  min-width: 80px;
}

.invoice-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: capitalize;
  min-width: 60px;
  text-align: center;
}

.invoice-status.paid {
  background: var(--color-green-button);
  color: white;
}

.invoice-status.open {
  background: var(--color-yellow-button);
  color: white;
}

.invoice-status.draft {
  background: var(--color-card-border);
  color: var(--text-primary);
}

.invoice-actions {
  display: flex;
  gap: 10px;
}

.invoice-link {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: var(--color-primary-button);
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.invoice-link:hover {
  background: var(--color-primary-button-hover);
  color: white;
}

.show-more-section {
  text-align: center;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid var(--color-card-border);
}

.show-more-button {
  background: transparent;
  border: 1px solid var(--color-primary-button);
  color: var(--color-primary-button);
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}

.show-more-button:hover {
  background: var(--color-primary-button);
  color: white;
}

/* Billing Edit Modal Styles */
.billing-edit-section {
  margin-bottom: 30px;
  padding: 20px;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 12px;
}

.billing-edit-section h4 {
  margin: 0 0 20px 0;
  color: var(--text-primary);
  font-size: 1.2rem;
  border-bottom: 1px solid var(--color-card-border);
  padding-bottom: 10px;
}

.modalLine {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.modalLine.half-width {
  flex: 1;
}

.modal-row {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}

.text-box-label {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.95rem;
}

.text-box-label .required {
  color: var(--color-red-button);
  font-weight: bold;
}

.modalLine input,
.modalLine select {
  padding: 12px 16px;
  border: 2px solid var(--color-card-border);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--text-primary);
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.modalLine input:focus,
.modalLine select:focus {
  outline: none;
  border-color: var(--color-primary-button);
}

.modalLine input::placeholder {
  color: var(--text-secondary);
}

.current-payment-display {
  font-family: monospace;
  font-size: 1.1rem;
  color: var(--text-primary);
  margin-bottom: 15px;
  padding: 10px;
  background: var(--color-card-background);
  border-radius: 6px;
  border: 1px solid var(--color-card-border);
}

.secondary-button {
  background: transparent;
  border: 2px solid var(--color-primary-button);
  color: var(--color-primary-button);
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}

.secondary-button:hover {
  background: var(--color-primary-button);
  color: white;
}

.info-text {
  color: var(--text-secondary);
  font-style: italic;
  margin: 10px 0;
}

@media (max-width: 768px) {
  .payment-method-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .payment-method-actions {
    width: 100%;
    justify-content: flex-end;
  }
  
  .invoice-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .invoice-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
    width: 100%;
  }
  
  .invoice-info > div {
    min-width: auto;
  }
  
  .invoice-actions {
    width: 100%;
    justify-content: flex-end;
  }
  
  .modal-row {
    flex-direction: column;
    gap: 0;
  }
  
  .billing-edit-section {
    padding: 15px;
  }
}

/* Payment Method Form Styles */
.payment-form-section {
  margin-bottom: 25px;
}

.payment-form-section h5 {
  margin: 0 0 15px 0;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-card-border);
}

.card-element-container {
  border: 2px solid var(--color-card-border);
  border-radius: 8px;
  padding: 16px;
  background: var(--color-background);
  transition: border-color 0.3s ease;
  margin-bottom: 10px;
}

.card-element-container:focus-within {
  border-color: var(--color-primary-button);
}

.card-element {
  min-height: 20px;
}

.error-message {
  color: var(--color-red-button);
  font-size: 0.875rem;
  margin-top: 8px;
  padding: 8px 12px;
  background: rgba(var(--color-red-button-rgb), 0.1);
  border-radius: 6px;
  border-left: 3px solid var(--color-red-button);
}

.payment-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid var(--color-card-border);
}

.primary-button, .secondary-button {
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid;
  min-width: 120px;
}

.primary-button {
  background: var(--color-primary-button);
  border-color: var(--color-primary-button-border);
  color: white;
}

.primary-button:hover:not(:disabled) {
  background: var(--color-primary-button-hover);
  border-color: var(--color-primary-button-hover);
}

.primary-button:disabled {
  background: var(--color-card-border);
  border-color: var(--color-card-border);
  cursor: not-allowed;
  opacity: 0.7;
}

.secondary-button {
  background: transparent;
  border-color: var(--color-primary-button);
  color: var(--color-primary-button);
}

.secondary-button:hover:not(:disabled) {
  background: var(--color-primary-button);
  color: white;
}

.secondary-button:disabled {
  border-color: var(--color-card-border);
  color: var(--color-card-border);
  cursor: not-allowed;
}

/* Checkbox Styles */
.checkbox-container {
  display: flex;
  align-items: center;
  gap: 12px;
  //margin: 15px 0;
  padding: 12px;
  background: rgba(var(--color-primary-button-rgb), 0.05);
  border-radius: 8px;
  border: 1px solid rgba(var(--color-primary-button-rgb), 0.2);
}

.checkbox-container input[type="checkbox"] {
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
  accent-color: var(--color-primary-button);
}

.checkbox-label {
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  user-select: none;
  margin: 0;
}

.checkbox-container input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.checkbox-container input[type="checkbox"]:disabled + .checkbox-label {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Current Address Display */
.current-address-display {
  margin: 0;
  padding: 16px;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 8px;
}

.address-preview {
  margin-top: 8px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.address-preview div {
  margin-bottom: 4px;
}

.address-preview div:last-child {
  margin-bottom: 0;
}

/* Mobile responsive for payment form */
@media (max-width: 768px) {
  .payment-form-actions {
    flex-direction: column-reverse;
    gap: 8px;
  }
  
  .primary-button, .secondary-button {
    width: 100%;
    min-width: auto;
  }
  
  .checkbox-container {
    //flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

/* Add Payment Method Button Styles */
.add-payment-method-section {
  margin-top: 1rem;
  padding: 1rem;
  border-top: 1px solid var(--color-card-border);
}

.add-payment-method-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--color-primary-button);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-payment-method-button:hover:not(:disabled) {
  background: var(--color-secondary-button);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.add-payment-method-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.payment-form-section {
  margin-bottom: 1.5rem;
}

.payment-form-section h5 {
  margin: 0 0 1rem 0;
  color: var(--text-primary);
  font-size: 1.2rem;
  font-weight: 500;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--color-card-border);
}

.current-address-display {
  background: var(--color-background-secondary);
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid var(--color-card-border);
}

.address-preview {
  margin-top: 0.5rem;
  line-height: 1.4;
}

.address-preview div {
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.required {
  color: var(--error-red);
}

/* Stripe Card Element Styling */
.stripe-card-element {
  padding: 12px 16px;
  border: 2px solid #444444;
  border-radius: 8px;
  background: transparent;
  transition: border-color 0.3s ease;
  min-height: 50px;
  display: block;
  position: relative;
}

.stripe-card-element:focus-within {
  border-color: #00bcd4;
}

.stripe-card-element.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Ensure Stripe Elements inherit the right styling */
.stripe-card-element .StripeElement {
  height: 100%;
  width: 100%;
}

/* Error state for the card element container */
.stripe-card-element.has-error {
  border-color: #fa755a;
}
</style>